import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';

import FormField from './FormField';

interface FormDateFieldProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    label: string;
    placeholder?: string;
    blockFutureDates?: boolean;
    /**
     * Earliest selectable day. Used by close / relocate / recordEvent / operate
     * to forbid picking a date before the current location of the FG(s)
     * involved — which the backend rejects with `INVALID_OPERATION_DATE`.
     */
    minDate?: Date | null;
    /**
     * Earliest day the calendar offers. Unlike `minDate`, an earlier typed day is kept, so
     * `rules` can refuse it with a message rather than the field reading as empty.
     */
    blockDaysBefore?: Date | null;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    errorMessage?: string;
    optional?: boolean;
}

export default function FormDateField<TFieldValues extends FieldValues>(
    props: FormDateFieldProps<TFieldValues>
) {
    const {
        control,
        name,
        label,
        placeholder,
        blockFutureDates,
        minDate,
        blockDaysBefore,
        rules,
        errorMessage,
        optional,
    } = props;
    const { t } = useTranslation('fishGroups');

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormField
                    label={label}
                    optional={optional}
                    error={
                        fieldState.error
                            ? fieldState.error.message ||
                              errorMessage ||
                              t('actions.errors.required')
                            : null
                    }>
                    <StyledSingleDatePicker
                        date={(field.value as Date | null | undefined) ?? null}
                        onDateChange={field.onChange}
                        placeholder={placeholder}
                        blockFutureDates={blockFutureDates}
                        minDate={minDate}
                        blockDaysBefore={blockDaysBefore}
                    />
                </FormField>
            )}
        />
    );
}
